import React, { useEffect, useState, useContext } from "react";
import { 
  Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, IconButton, CircularProgress, TablePagination 
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import PageSearchBar from "../../common/PageSearchBar";
import { listTags } from "../../utils/blogApi";
import ModalContext from "../../context/ModalContext.js";
import DrawerContext from "../../context/DrawerContext.js";
import AuthContext from "../../context/AuthContext.js";
import "../../Pages/Tags/Tags.scss";

const Tags = () => {
  const authCtx = useContext(AuthContext);
  const modalCtx = useContext(ModalContext);
  const drawerCtx = useContext(DrawerContext);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => { getAllTags(); }, []);
  useEffect(() => { getAllTags(); }, [drawerCtx, modalCtx]);
  useEffect(() => { setPage(0); }, [searchInput]);

  const getAllTags = () => {
    listTags()
      .then((data) => setTags(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error(err);
        setTags([]);
        setLoadError(err.message);
      })
      .finally(() => setLoading(false));
  };

  const openDeleteTagModal = (tag) => {
    modalCtx.setDetails("delete-tag", { slug: tag.slug, name: tag.name });
    modalCtx.openModal();
  };

  const toggleDrawerHandler = (drawer, data = {}) => {
    drawerCtx.setDetails(drawer, { type: drawer, ...data }); 
    drawerCtx.openDrawer();
  };

  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => { 
    setRowsPerPage(Math.max(10, parseInt(event.target.value, 10))); 
    setPage(0); 
  };

  const filteredTags = tags.filter((tag) => {
    const term = (searchInput || "").toLowerCase().trim();
    if (!term) return true;
    const name = (tag.name || "").toLowerCase();
    return name.includes(term);
  });

  const paginatedTags = filteredTags.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div className="tags-container">
      <div className="header-section">
        <span className="title">TAGS</span>
        <div className="header-items-div">
          <PageSearchBar value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search" />
          <Button variant="contained" className="add-button" onClick={() => toggleDrawerHandler("create-tag")}>+ ADD TAG</Button>
        </div>
      </div>
      <section className="tags-table-container">
        <TableContainer component={Paper} className="tags-table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className="tags-head-cell">Tag Name</TableCell>
                <TableCell className="tags-head-cell" align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ border: "0px" }}><CircularProgress size={48} style={{ color: "#e84118",border: "0px" }} /></TableCell>
                </TableRow>
              ) : filteredTags.length > 0 ? (
                <>
                  {paginatedTags.map((tag) => (
                    <TableRow key={tag.slug} className="tags-table-row">
                      <TableCell className="tags-table-cell">{tag.name || "Unnamed Tag"}</TableCell>
                      <TableCell className="tags-table-cell" align="right">
                        <div className="action-buttons">
                          <IconButton className="action-icon" size="small" onClick={() => toggleDrawerHandler("edit-tag", tag)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton className="action-icon" size="small" onClick={() => openDeleteTagModal(tag)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    {loadError || (tags.length === 0 ? "No Tags Found" : "No matching tags")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </section>
      <TableContainer component={Paper} className="tags-pagination">
        <Table>
          <TableBody>
            <TableRow>
              <TablePagination 
                rowsPerPageOptions={[10, 25, 50]} 
                colSpan={3} 
                count={filteredTags.length} 
                rowsPerPage={rowsPerPage} 
                page={page}
                onPageChange={handleChangePage} 
                onRowsPerPageChange={handleChangeRowsPerPage} 
              />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default Tags;
